import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { companiesAPI, itmReportAPI } from '../services/api';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import EmptyState from '../components/ui/EmptyState';
import { formatDateTime } from '../utils/dateFormat';
import SearchableSelect from '../components/ui/SearchableSelect';
import HelpTooltip from '../components/ui/HelpTooltip';
import ComplianceCell from '../components/reports/ComplianceCell';
import ComplianceSummary from '../components/reports/ComplianceSummary';

const triggerDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const PdfIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);
const ExcelIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
    />
  </svg>
);
const SignatureIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11 5h2M12 5v6m-7 8c2-3 4-4.5 7-4.5S17 16 19 19M9 11l3 3 3-3"
    />
  </svg>
);

const GlobalReports = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState(null);

  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  // Filtre tabel
  const [deptFilter, setDeptFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'nonCompliant'

  // Selectorul de firmă apare doar pentru rolurile care administrează mai multe firme.
  const isMultiCompany = ['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(
    user?.role
  );

  // ── Încarcă firmele accesibile ──
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await companiesAPI.getAll();
        const list = res.data || [];
        setCompanies(list);
        // Pentru rolurile mono-firmă (COMPANY_ADMIN / DIRECTOR_GENERAL) auto-selectăm firma proprie.
        if (list.length === 1) {
          setSelectedCompanyId(list[0].id);
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, []);

  // ── Încarcă raportul pentru firma selectată ──
  const fetchReport = useCallback(
    async (companyId) => {
      if (!companyId) {
        setReport(null);
        return;
      }
      setLoadingReport(true);
      setError(null);
      setDeptFilter('');
      setStatusFilter('all');
      try {
        const res = await itmReportAPI.getData(companyId);
        setReport(res.data);
      } catch (err) {
        setReport(null);
        setError(err.response?.data?.error || t('globalReports.loadError'));
      } finally {
        setLoadingReport(false);
      }
    },
    [t]
  );

  // Firma selectată (din lista încărcată) — folosită ca să afișăm upgrade-state-ul
  // pentru add-on-ul ITM înainte chiar de a primi 403 de la server.
  // NB: declarate ÎNAINTE de useEffect — dependency array-ul de mai jos le citește la render
  // (altfel: TDZ „Cannot access 'itmNotEnabled' before initialization").
  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === selectedCompanyId) || null,
    [companies, selectedCompanyId]
  );
  const itmNotEnabled =
    !!selectedCompanyId && selectedCompany && selectedCompany.offersITM === false;

  useEffect(() => {
    // Nu cerem raportul dacă firma nu are add-on-ul ITM — afișăm direct upgrade-state-ul,
    // evităm un 403 inutil. Curățăm raportul vechi când se schimbă pe o firmă neactivată.
    if (selectedCompanyId && !itmNotEnabled) {
      fetchReport(selectedCompanyId);
    } else if (itmNotEnabled) {
      setReport(null);
      setError(null);
    }
  }, [selectedCompanyId, itmNotEnabled, fetchReport]);

  const companyOptions = useMemo(
    () =>
      companies.map((c) => ({
        value: c.id,
        // Firmele fără add-on ITM rămân selectabile, dar sunt marcate clar „add-on neactivat".
        label: c.offersITM === false ? `${c.name} — ${t('itmService.addonNotActive')}` : c.name,
        subtitle: c.cui ? `CUI: ${c.cui}` : undefined,
      })),
    [companies, t]
  );

  const showSSM = !!report?.company?.offersSSM;
  const showSU = !!report?.company?.offersSU;

  // Departamentele prezente în raport pentru filtrul de departament.
  const deptOptions = useMemo(() => {
    if (!report?.employees) return [];
    const set = new Set(report.employees.map((e) => e.departmentName).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [report]);

  // Un angajat e „neconform" dacă are măcar o categorie aplicabilă lipsă/parțială.
  const isNonCompliant = useCallback(
    (e) => {
      const keys = [];
      if (showSSM) keys.push('IIG_SSM', 'ILM_SSM', 'IP_SSM');
      if (showSU) keys.push('IIG_SU', 'ILM_SU', 'IP_SU');
      return keys.some((k) => ['missing', 'partial'].includes(e.compliance?.[k]));
    },
    [showSSM, showSU]
  );

  const filteredEmployees = useMemo(() => {
    if (!report?.employees) return [];
    let list = [...report.employees].sort((a, b) =>
      (a.fullName || '').localeCompare(b.fullName || '')
    );
    if (deptFilter) list = list.filter((e) => e.departmentName === deptFilter);
    if (statusFilter === 'nonCompliant') list = list.filter(isNonCompliant);
    return list;
  }, [report, deptFilter, statusFilter, isNonCompliant]);

  const handleExportPDF = async () => {
    if (!selectedCompanyId) return;
    setExportingPdf(true);
    try {
      const res = await itmReportAPI.downloadPDF(selectedCompanyId);
      const safeName = (report?.company?.name || 'firma').replace(/[^a-zA-Z0-9]/g, '-');
      triggerDownload(
        new Blob([res.data], { type: 'application/pdf' }),
        `raport-ITM-${safeName}.pdf`
      );
      showToast(t('globalReports.exportPDFDone'), 'success');
    } catch (err) {
      showToast(err.response?.data?.error || t('globalReports.exportPDFError'), 'error');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    if (!selectedCompanyId) return;
    setExportingExcel(true);
    try {
      const res = await itmReportAPI.downloadExcel(selectedCompanyId);
      const safeName = (report?.company?.name || 'firma').replace(/[^a-zA-Z0-9]/g, '-');
      triggerDownload(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `raport-ITM-${safeName}.xlsx`
      );
      showToast(t('globalReports.exportExcelDone'), 'success');
    } catch (err) {
      showToast(err.response?.data?.error || t('globalReports.exportExcelError'), 'error');
    } finally {
      setExportingExcel(false);
    }
  };

  if (loadingCompanies) {
    return <Spinner label={t('globalReports.loading')} />;
  }

  // Raportul oficial cu dublă semnătură (admin + SSM) se inițiază din tab-ul ITM al firmei.
  const goToOfficialReport = () => {
    if (!selectedCompanyId) return;
    navigate(`/companies/${selectedCompanyId}?tab=itm`);
  };

  const headerActions = report ? (
    <>
      <Button
        variant="outline"
        onClick={handleExportExcel}
        loading={exportingExcel}
        disabled={loadingReport}
      >
        <ExcelIcon /> {t('globalReports.exportExcel')}
      </Button>
      <Button
        variant="outline"
        onClick={handleExportPDF}
        loading={exportingPdf}
        disabled={loadingReport}
      >
        <PdfIcon /> {t('globalReports.exportPDF')}
      </Button>
      <Button variant="primary" onClick={goToOfficialReport} disabled={loadingReport}>
        <SignatureIcon /> {t('globalReports.officialReport')}
      </Button>
    </>
  ) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('globalReports.title')}
        subtitle={t('globalReports.subtitle')}
        actions={headerActions}
      />

      {/* Selector firmă + filtre */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {isMultiCompany && (
            <SearchableSelect
              label={t('globalReports.selectCompany')}
              placeholder={t('globalReports.selectCompanyPlaceholder')}
              value={selectedCompanyId}
              onChange={setSelectedCompanyId}
              options={companyOptions}
            />
          )}
          {report && (
            <>
              <div>
                <label className="input-label">{t('globalReports.filterDept')}</label>
                <select
                  className="input w-full"
                  value={deptFilter}
                  onChange={(e) => setDeptFilter(e.target.value)}
                >
                  <option value="">{t('globalReports.filterAll')}</option>
                  {deptOptions.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="input-label">{t('globalReports.filterStatus')}</label>
                <select
                  className="input w-full"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">{t('globalReports.filterStatusAll')}</option>
                  <option value="nonCompliant">
                    {t('globalReports.filterStatusNonCompliant')}
                  </option>
                </select>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Stări fără selecție / fără firme */}
      {!isMultiCompany && companies.length === 0 && (
        <EmptyState title={t('globalReports.noCompanies')} />
      )}
      {isMultiCompany && !selectedCompanyId && (
        <Card>
          <p className="text-slate-500 dark:text-slate-400 text-center py-8">
            {companies.length === 0
              ? t('globalReports.noCompanies')
              : t('globalReports.selectPrompt')}
          </p>
        </Card>
      )}

      {/* Upgrade-state — firma selectată nu are add-on-ul Raport ITM activat. */}
      {itmNotEnabled && (
        <Card>
          <div className="flex flex-col items-center text-center py-10 px-4">
            <div className="w-14 h-14 rounded-full bg-violet-100 dark:bg-violet-900/50 flex items-center justify-center mb-4">
              <svg
                className="w-7 h-7 text-violet-600 dark:text-violet-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
              {t('itmService.upgradeTitle')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mb-5">
              {t('itmService.upgradeDesc')}
            </p>
            <a href="mailto:contact@platforma-ssm.ro?subject=Activare%20Raport%20ITM">
              <Button variant="primary">{t('itmService.contactToActivate')}</Button>
            </a>
          </div>
        </Card>
      )}

      {loadingReport && <Spinner label={t('globalReports.loading')} />}

      {error && !loadingReport && (
        <Card>
          <p className="text-danger-600 dark:text-danger-300 text-center py-6">{error}</p>
        </Card>
      )}

      {/* Conținut raport */}
      {report && !loadingReport && (
        <>
          {/* Antet firmă + meta */}
          <Card>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                  {report.company?.name}
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {report.company?.cui ? `CUI: ${report.company.cui}` : ''}
                  {report.company?.address ? ` · ${report.company.address}` : ''}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {showSSM && <Badge color="blue">SSM</Badge>}
                  {showSU && <Badge color="red">SU</Badge>}
                  {report.company?.parentCompany && (
                    <Badge color="gray">{report.company.parentCompany.name}</Badge>
                  )}
                </div>
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400 sm:text-right">
                <p>
                  {t('globalReports.reportNumber')}:{' '}
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {report.reportNumber}
                  </span>
                </p>
                <p>
                  {t('globalReports.generatedAt')}:{' '}
                  {report.generatedAt ? formatDateTime(report.generatedAt) : '—'}
                </p>
              </div>
            </div>
          </Card>

          {/* Sinteză */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
              {t('globalReports.summaryTitle')}
            </h3>
            <ComplianceSummary summary={report.summary} />
          </div>

          {/* Tabel angajați */}
          <Card padding={false}>
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 inline-flex items-center gap-1">
                {t('globalReports.tableTitle')}
                <HelpTooltip text={t('common.glossaryAbbr')} size={14} />
              </h3>
              <span className="text-xs text-slate-400 dark:text-slate-400">
                {filteredEmployees.length}
              </span>
            </div>

            {filteredEmployees.length === 0 ? (
              <p className="text-slate-500 dark:text-slate-400 text-center py-8">
                {t('globalReports.noEmployees')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800">
                      <th className="px-3 py-2 font-medium">{t('globalReports.colNr')}</th>
                      <th className="px-3 py-2 font-medium">{t('globalReports.colName')}</th>
                      <th className="px-3 py-2 font-medium">{t('globalReports.colJob')}</th>
                      <th className="px-3 py-2 font-medium">{t('globalReports.colDept')}</th>
                      {showSSM && (
                        <>
                          <th className="px-2 py-2 font-medium text-center text-primary-600 dark:text-primary-300">
                            IIG
                          </th>
                          <th className="px-2 py-2 font-medium text-center text-primary-600 dark:text-primary-300">
                            ILM
                          </th>
                          <th className="px-2 py-2 font-medium text-center text-primary-600 dark:text-primary-300">
                            IP
                          </th>
                          <th className="px-2 py-2 font-medium text-center text-primary-600 dark:text-primary-300">
                            {t('globalReports.colAdmitted')}
                          </th>
                        </>
                      )}
                      {showSU && (
                        <>
                          <th className="px-2 py-2 font-medium text-center text-danger-600 dark:text-danger-300">
                            IIG
                          </th>
                          <th className="px-2 py-2 font-medium text-center text-danger-600 dark:text-danger-300">
                            ILM
                          </th>
                          <th className="px-2 py-2 font-medium text-center text-danger-600 dark:text-danger-300">
                            IP
                          </th>
                          <th className="px-2 py-2 font-medium text-center text-danger-600 dark:text-danger-300">
                            {t('globalReports.colAdmitted')}
                          </th>
                        </>
                      )}
                      <th className="px-2 py-2 font-medium text-center">
                        {t('globalReports.colEPI')}
                      </th>
                    </tr>
                    {(showSSM || showSU) && (
                      <tr className="text-[10px] uppercase tracking-wide border-b border-slate-100 dark:border-slate-800">
                        <th colSpan={4}></th>
                        {showSSM && (
                          <th
                            colSpan={4}
                            className="px-2 py-1 text-center text-primary-500 dark:text-primary-300"
                          >
                            SSM
                          </th>
                        )}
                        {showSU && (
                          <th
                            colSpan={4}
                            className="px-2 py-1 text-center text-danger-500 dark:text-danger-300"
                          >
                            SU
                          </th>
                        )}
                        <th></th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {filteredEmployees.map((e, i) => (
                      <tr
                        key={e.employeeId}
                        className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30"
                      >
                        <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                        <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-200">
                          {e.fullName || '—'}
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                          {e.jobTitle || '—'}
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                          {e.departmentName || '—'}
                        </td>
                        {showSSM && (
                          <>
                            <td className="px-2 py-2 text-center">
                              <ComplianceCell status={e.compliance?.IIG_SSM} />
                            </td>
                            <td className="px-2 py-2 text-center">
                              <ComplianceCell status={e.compliance?.ILM_SSM} />
                            </td>
                            <td className="px-2 py-2 text-center">
                              <ComplianceCell status={e.compliance?.IP_SSM} />
                            </td>
                            <td className="px-2 py-2 text-center">
                              {e.compliance?.admittedSSM ? (
                                <span className="text-accent-600 dark:text-accent-300 font-semibold">
                                  ✓
                                </span>
                              ) : (
                                <span className="text-slate-300 dark:text-slate-600">—</span>
                              )}
                            </td>
                          </>
                        )}
                        {showSU && (
                          <>
                            <td className="px-2 py-2 text-center">
                              <ComplianceCell status={e.compliance?.IIG_SU} />
                            </td>
                            <td className="px-2 py-2 text-center">
                              <ComplianceCell status={e.compliance?.ILM_SU} />
                            </td>
                            <td className="px-2 py-2 text-center">
                              <ComplianceCell status={e.compliance?.IP_SU} />
                            </td>
                            <td className="px-2 py-2 text-center">
                              {e.compliance?.admittedSU ? (
                                <span className="text-accent-600 dark:text-accent-300 font-semibold">
                                  ✓
                                </span>
                              ) : (
                                <span className="text-slate-300 dark:text-slate-600">—</span>
                              )}
                            </td>
                          </>
                        )}
                        <td className="px-2 py-2 text-center">
                          {e.hasEPI ? (
                            <span className="text-accent-600 dark:text-accent-300 font-semibold">
                              ✓
                            </span>
                          ) : (
                            <span className="text-slate-300 dark:text-slate-600">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Legendă */}
            <div className="px-5 py-3 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
              <span className="font-medium">{t('globalReports.legend')}</span>
              <span className="flex items-center gap-1">
                <ComplianceCell status="ok" /> {t('globalReports.legendOk')}
              </span>
              <span className="flex items-center gap-1">
                <ComplianceCell status="missing" /> {t('globalReports.legendMissing')}
              </span>
              <span className="flex items-center gap-1">
                <ComplianceCell status="partial" /> {t('globalReports.legendPartial')}
              </span>
              <span className="flex items-center gap-1">
                <ComplianceCell status="na" /> {t('globalReports.legendNa')}
              </span>
            </div>
          </Card>

          <p className="text-xs text-slate-400 dark:text-slate-400">
            {t('globalReports.snapshotNote')}
          </p>
        </>
      )}
    </div>
  );
};

export default GlobalReports;
